'use client';

import React, { useState } from 'react';
import Link from 'next/link';

export default function InkRefillLandingPage() {
  const [formData, setFormData] = useState({ name: '', phone: '', city: '', printerModel: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white font-sans" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Top bar */}
      <div className="bg-[#1a1a2e] text-white text-sm py-2 px-4 text-center">
        📞 Book Now: <a href="tel:+12148529965" className="underline font-semibold">214-852-9965</a> &nbsp;|&nbsp; Same-day service available in Dallas, Houston, Austin & more
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}>
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17H7a4 4 0 01-4-4V7a4 4 0 014-4h10a4 4 0 014 4v6a4 4 0 01-4 4zm0 0v2a2 2 0 01-2 2H9a2 2 0 01-2-2v-2" />
              </svg>
            </div>
            <div>
              <div className="font-bold text-gray-900 text-lg leading-tight">Printeasy Hub</div>
              <div className="text-xs text-gray-500">Onsite Ink Refill & Replacement</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-600">
            <a href="#services" className="hover:text-red-600 transition-colors">Services</a>
            <a href="#how-it-works" className="hover:text-red-600 transition-colors">How It Works</a>
            <a href="#pricing" className="hover:text-red-600 transition-colors">Pricing</a>
            <a href="#contact" className="hover:text-red-600 transition-colors">Contact</a>
          </nav>
          <a href="tel:+12148529965" className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z" />
            </svg>
            Call Now
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, #e63946 0%, transparent 70%)' }}></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, #0096D6 0%, transparent 70%)' }}></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-6" style={{ background: 'rgba(230,57,70,0.2)', color: '#ff6b7a', border: '1px solid rgba(230,57,70,0.3)' }}>
                ✅ Certified Technicians · Doorstep Service
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                Printer Ink Refill &amp; Replacement — <span style={{ color: '#ff6b7a' }}>At Your Doorstep</span>
              </h1>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                No need to carry your printer anywhere. Our certified technicians come to your home or office for onsite ink refill, cartridge replacement, and printer servicing — same day, guaranteed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#book-now" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-base transition-transform hover:scale-105" style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}>
                  Book Doorstep Service
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
                <a href="tel:+12148529965" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-base border border-white/30 text-white hover:bg-white/10 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z" /></svg>
                  Call 214-852-9965
                </a>
              </div>
              <div className="flex flex-wrap gap-6 mt-8">
                {[['500+', 'Happy Customers'], ['4.9★', 'Google Rating'], ['Same Day', 'Service']].map(([val, label]) => (
                  <div key={label} className="text-center">
                    <div className="text-2xl font-bold text-white">{val}</div>
                    <div className="text-xs text-gray-400">{label}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Visual card */}
            <div className="hidden md:block">
              <div className="rounded-2xl p-6 border border-white/10" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
                <div className="text-white font-semibold mb-4 text-lg">We Service All Major Brands</div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { brand: 'Canon', color: '#cc0000', icon: '🖨️' },
                    { brand: 'HP', color: '#0096D6', icon: '🖨️' },
                    { brand: 'Epson', color: '#00529b', icon: '🖨️' },
                    { brand: 'Brother', color: '#004b87', icon: '🖨️' },
                    { brand: 'Samsung', color: '#1428a0', icon: '🖨️' },
                    { brand: 'Lexmark', color: '#e8000d', icon: '🖨️' },
                  ].map(({ brand, color, icon }) => (
                    <div key={brand} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.07)' }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-lg" style={{ background: color + '22' }}>{icon}</div>
                      <span className="text-white text-sm font-medium">{brand}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-xl text-center text-sm" style={{ background: 'rgba(230,57,70,0.15)', color: '#ff6b7a', border: '1px solid rgba(230,57,70,0.2)' }}>
                  + All other inkjet &amp; laser printers
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-gray-50 border-b border-gray-100 py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            {[
              ['🏠', 'Doorstep Service'],
              ['⚡', 'Same-Day Booking'],
              ['🔧', 'Certified Technicians'],
              ['💯', '100% Genuine Ink'],
              ['🛡️', '30-Day Service Warranty'],
              ['📍', 'Nationwide Coverage'],
            ].map(([icon, text]) => (
              <div key={text} className="flex items-center gap-2 font-medium">
                <span className="text-lg">{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Our Onsite Printer Services</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">Professional printer care delivered to your door. No hassle, no downtime.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: '🖋️',
                title: 'Ink Cartridge Refill',
                desc: 'Refill your existing cartridges with high-quality ink. Works for all inkjet printers — Canon, HP, Epson, Brother.',
                badge: 'Most Popular',
                badgeColor: '#e63946',
              },
              {
                icon: '🔄',
                title: 'Cartridge Replacement',
                desc: 'Replace old or damaged cartridges with genuine OEM or compatible cartridges. Full color and black options available.',
                badge: null,
                badgeColor: null,
              },
              {
                icon: '🖨️',
                title: 'Toner Refill (Laser)',
                desc: 'Laser printer toner refill and replacement for HP LaserJet, Canon imageCLASS, Brother, and Samsung printers.',
                badge: null,
                badgeColor: null,
              },
              {
                icon: '🔧',
                title: 'Printer Head Cleaning',
                desc: 'Clogged print heads cause streaky prints. Our technicians clean and restore your print quality on-site.',
                badge: null,
                badgeColor: null,
              },
              {
                icon: '📶',
                title: 'Wi-Fi Setup & Config',
                desc: 'Get your printer connected to your home or office Wi-Fi network. Works with all wireless-capable printers.',
                badge: null,
                badgeColor: null,
              },
              {
                icon: '🛠️',
                title: 'General Printer Repair',
                desc: 'Paper jams, error codes, driver issues — our technicians diagnose and fix common printer problems on-site.',
                badge: null,
                badgeColor: null,
              },
            ].map(({ icon, title, desc, badge, badgeColor }) => (
              <div key={title} className="relative p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow bg-white group">
                {badge && (
                  <div className="absolute -top-3 left-6 px-3 py-1 rounded-full text-white text-xs font-bold" style={{ background: badgeColor || '#e63946' }}>
                    {badge}
                  </div>
                )}
                <div className="text-3xl mb-4">{icon}</div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20" style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)' }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">How It Works</h2>
            <p className="text-gray-500 text-lg">Simple, fast, and hassle-free printer service at your doorstep</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', icon: '📱', title: 'Book Online or Call', desc: 'Fill the form below or call us directly. Tell us your printer model and issue.' },
              { step: '02', icon: '📅', title: 'Schedule a Visit', desc: 'Choose a time slot that works for you — morning, afternoon, or evening.' },
              { step: '03', icon: '🏠', title: 'Technician Arrives', desc: 'Our certified technician arrives at your home or office with all required tools and ink.' },
              { step: '04', icon: '✅', title: 'Job Done!', desc: 'Service completed on-site. Test print done before the technician leaves.' },
            ].map(({ step, icon, title, desc }) => (
              <div key={step} className="text-center p-6 rounded-2xl bg-white shadow-sm border border-gray-100">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}>
                  {step}
                </div>
                <div className="text-3xl mb-3">{icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Transparent Pricing</h2>
            <p className="text-gray-500 text-lg">No hidden charges. What you see is what you pay.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Basic Ink Refill',
                price: '$29',
                desc: 'Single color or black cartridge refill',
                features: ['1 cartridge refill', 'Test print included', 'Doorstep service', '15-day warranty'],
                highlight: false,
              },
              {
                name: 'Full Color Refill',
                price: '$59',
                desc: 'All color + black cartridges refilled',
                features: ['All cartridges refilled', 'Print head check', 'Test print included', 'Doorstep service', '30-day warranty'],
                highlight: true,
              },
              {
                name: 'Complete Service',
                price: '$99',
                desc: 'Full printer service + ink refill',
                features: ['All cartridges refilled', 'Print head cleaning', 'Wi-Fi setup if needed', 'Driver installation', 'Doorstep service', '30-day warranty'],
                highlight: false,
              },
            ].map(({ name, price, desc, features, highlight }) => (
              <div key={name} className={`p-6 rounded-2xl border-2 ${highlight ? 'border-red-500 shadow-xl' : 'border-gray-100'} relative`}>
                {highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}>
                    Best Value
                  </div>
                )}
                <h3 className="font-bold text-gray-900 text-xl mb-1">{name}</h3>
                <p className="text-gray-500 text-sm mb-4">{desc}</p>
                <div className="text-4xl font-bold mb-6" style={{ color: highlight ? '#e63946' : '#1a1a2e' }}>{price}</div>
                <ul className="space-y-2 mb-6">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#e63946' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="#book-now" className={`block text-center py-3 rounded-xl font-semibold text-sm transition-transform hover:scale-105 ${highlight ? 'text-white' : 'text-red-600 border-2 border-red-500 hover:bg-red-50'}`} style={highlight ? { background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' } : {}}>
                  Book This Plan
                </a>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 text-sm mt-6">* Prices may vary based on printer model and location. Final price confirmed before service.</p>
        </div>
      </section>

      {/* Booking Form */}
      <section id="book-now" className="py-20" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Book Doorstep Service</h2>
            <p className="text-gray-300">Fill in your details and we'll confirm your appointment within 30 minutes.</p>
          </div>
          {submitted ? (
            <div className="bg-white rounded-2xl p-10 text-center">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking Received!</h3>
              <p className="text-gray-500">Thank you! Our team will call you within 30 minutes to confirm your appointment.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    placeholder="(214) 555-1234"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">City / State *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Dallas, Houston, Austin"
                    value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Printer Model</label>
                  <input
                    type="text"
                    placeholder="e.g., Canon TS3755, HP ENVY 6055"
                    value={formData.printerModel}
                    onChange={e => setFormData({ ...formData, printerModel: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Describe Your Issue</label>
                <textarea
                  rows={3}
                  placeholder="e.g., Ink is running low, cartridge not recognized, streaky prints..."
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                />
              </div>
              <button
                type="submit"
                className="w-full py-4 rounded-xl text-white font-bold text-base transition-transform hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}
              >
                Book Doorstep Service Now →
              </button>
              <p className="text-center text-xs text-gray-400">
                By submitting, you agree to our{' '}
                <Link href="/privacy-policy" className="underline hover:text-gray-600">Privacy Policy</Link>
                {' '}and{' '}
                <Link href="/terms-of-service" className="underline hover:text-gray-600">Terms of Service</Link>.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What Our Customers Say</h2>
            <p className="text-gray-500">Real reviews from real customers across the USA</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Sarah Mitchell', city: 'Dallas, TX', rating: 5, text: 'Excellent service! The technician came within 2 hours and refilled my Canon printer ink. Prints are perfect now. Highly recommended!' },
              { name: 'James Carter', city: 'Houston, TX', rating: 5, text: 'Very professional. Fixed my HP printer cartridge issue on the spot. No need to carry the printer anywhere. Great value for money.' },
              { name: 'Emily Rodriguez', city: 'Austin, TX', rating: 5, text: 'Booked online, technician arrived on time, cleaned the print head and refilled ink. My Epson printer works like new. 5 stars!' },
            ].map(({ name, city, rating, text }) => (
              <div key={name} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: rating }).map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">"{text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}>
                    {name[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{name}</div>
                    <div className="text-gray-400 text-xs">{city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Contact Us</h2>
          <p className="text-gray-500 mb-8">Reach us via phone, email, or visit our office</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: '📞', label: 'Phone', value: '214-852-9965', href: 'tel:+12148529965' },
              { icon: '✉️', label: 'Email', value: 'info@printeasyhub.online', href: 'mailto:info@printeasyhub.online' },
              { icon: '📍', label: 'Address', value: 'TX, USA', href: '#' },
            ].map(({ icon, label, value, href }) => (
              <a key={label} href={href} className="p-6 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow block text-center">
                <div className="text-3xl mb-3">{icon}</div>
                <div className="font-semibold text-gray-900 mb-1">{label}</div>
                <div className="text-gray-500 text-sm">{value}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-white font-bold text-lg mb-1">Printeasy Hub</div>
              <div className="text-sm">Onsite Printer Ink Refill &amp; Replacement Service</div>
              <div className="text-sm mt-1">TX, USA &nbsp;|&nbsp; aprinteasyhub.online</div>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link href="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
              <Link href="/contact-us" className="hover:text-white transition-colors">Contact Us</Link>
            </nav>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-xs text-gray-600">
            © {new Date().getFullYear()} Printeasy Hub. All rights reserved. We are an independent printer service provider and are not affiliated with Canon, HP, Epson, or any other printer manufacturer.
          </div>
        </div>
      </footer>
    </div>
  );
}
